import type { BookingDurationMinutes, FlowType } from '@/types/booking'

export type BookingTimeWindow = {
  start: string // HH:mm
  end: string // HH:mm
  label?: string
}

export type BookingAvailabilityConfig = {
  bookableWindows: readonly BookingTimeWindow[]
  breakWindows: readonly BookingTimeWindow[]
  timelineStart?: string
  timelineEnd?: string

  /**
   * Appointment durations shown to patients for this clinician/context.
   */
  allowedDurations: readonly BookingDurationMinutes[]

  /**
   * Default duration selected when the slot step opens.
   */
  defaultDuration: BookingDurationMinutes
}

/**
 * Default public booking availability.
 *
 * This is intentionally clinic-level config, not UI logic.
 * Change this file when ReACH working hours, lunch, public availability,
 * or clinician duration rules change.
 */
const DEFAULT_BOOKABLE_WINDOWS: readonly BookingTimeWindow[] = [
  { start: '09:30', end: '17:00' },
]

const DEFAULT_BREAK_WINDOWS: readonly BookingTimeWindow[] = [
  { start: '13:30', end: '14:00', label: 'Lunch / break' },
]

const DEFAULT_ALLOWED_DURATIONS: readonly BookingDurationMinutes[] = [30, 60]
const DEFAULT_DURATION: BookingDurationMinutes = 60

const DR_MEENA_BOOKABLE_WINDOWS: readonly BookingTimeWindow[] = [
  { start: '09:30', end: '17:00' },
]

const DR_MEENA_BREAK_WINDOWS: readonly BookingTimeWindow[] =
  DEFAULT_BREAK_WINDOWS

const DR_MEENA_ALLOWED_DURATIONS: readonly BookingDurationMinutes[] = [15]
const DR_MEENA_DEFAULT_DURATION: BookingDurationMinutes = 15

function isDrMeena(doctorID?: string | null, doctorName?: string | null) {
  return (
    String(doctorID ?? '').trim() === '1' || /meena/i.test(doctorName ?? '')
  )
}

export function getBookingAvailabilityForDoctor({
  doctorID,
  doctorName,
  flowType,
}: {
  doctorID?: string | null
  doctorName?: string | null
  flowType?: FlowType | null
}): BookingAvailabilityConfig {
  // Reserved for future flow-specific windows/durations.
  // Example later:
  // if (flowType === 'Online') return ONLINE_ONLY_WINDOWS
  void flowType

  if (isDrMeena(doctorID, doctorName)) {
    return {
      bookableWindows: DR_MEENA_BOOKABLE_WINDOWS,
      breakWindows: DR_MEENA_BREAK_WINDOWS,
      timelineStart: '09:30',
      timelineEnd: '17:00',
      allowedDurations: DR_MEENA_ALLOWED_DURATIONS,
      defaultDuration: DR_MEENA_DEFAULT_DURATION,
    }
  }

  return {
    bookableWindows: DEFAULT_BOOKABLE_WINDOWS,
    breakWindows: DEFAULT_BREAK_WINDOWS,
    timelineStart: '09:30',
    timelineEnd: '17:00',
    allowedDurations: DEFAULT_ALLOWED_DURATIONS,
    defaultDuration: DEFAULT_DURATION,
  }
}
